import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { X, Settings, Cookie } from "lucide-react";

type ConsentType = "all" | "necessary" | "custom" | null;

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const CONSENT_KEY = "ganiga_cookie_consent";
const PREFERENCES_KEY = "ganiga_cookie_preferences";

export const getCookieConsent = (): ConsentType => {
  if (typeof window === "undefined") return null;
  return localStorage.getItem(CONSENT_KEY) as ConsentType;
};

export const getCookiePreferences = (): CookiePreferences => {
  if (typeof window === "undefined") {
    return { necessary: true, analytics: false, marketing: false };
  }
  const stored = localStorage.getItem(PREFERENCES_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  return { necessary: true, analytics: false, marketing: false };
};

export const canLoadAnalytics = (): boolean => {
  const consent = getCookieConsent();
  if (!consent) return false;
  if (consent === "all") return true;
  if (consent === "necessary") return false;
  const prefs = getCookiePreferences();
  return prefs.analytics;
};

export const canLoadMarketing = (): boolean => {
  const consent = getCookieConsent();
  if (!consent) return false;
  if (consent === "all") return true;
  if (consent === "necessary") return false;
  const prefs = getCookiePreferences();
  return prefs.marketing;
};

// Google Analytics Measurement ID
const GA_MEASUREMENT_ID = "G-LRF83SFKMP";

// Initialize gtag function
const initGtag = () => {
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag(...args: any[]) {
    window.dataLayer.push(arguments);
  };
};

// Initialize Google Consent Mode v2 with default denied state
export const initGoogleConsentMode = () => {
  initGtag();
  
  // Set default consent state (denied for analytics/ads, but allows anonymous pings)
  window.gtag("consent", "default", {
    analytics_storage: "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });

  // Enable URL passthrough for better attribution without cookies
  window.gtag("set", "url_passthrough", true);
  
  // Enable ads data redaction when consent denied
  window.gtag("set", "ads_data_redaction", true);
};

// Load Google Analytics script (loads once, consent mode controls behavior)
export const loadGoogleAnalytics = () => {
  // Check if already loaded
  if (document.querySelector(`script[src*="googletagmanager.com/gtag"]`)) return;

  const script = document.createElement("script");
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  script.async = true;
  document.head.appendChild(script);

  script.onload = () => {
    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID, { 
      anonymize_ip: true,
      send_page_view: true 
    });
  };
};

// Update consent state when user makes a choice
export const updateGoogleConsent = (analyticsGranted: boolean, marketingGranted: boolean) => {
  if (!window.gtag) return;
  
  window.gtag("consent", "update", {
    analytics_storage: analyticsGranted ? "granted" : "denied",
    ad_storage: marketingGranted ? "granted" : "denied",
    ad_user_data: marketingGranted ? "granted" : "denied",
    ad_personalization: marketingGranted ? "granted" : "denied",
  });
};

// Function to load Facebook Pixel only after consent
export const loadFacebookPixel = (pixelId: string) => {
  if (!canLoadMarketing()) return;
  
  // Check if already loaded
  if (window.fbq) return;

  (function(f: any, b: any, e: any, v: any, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function() {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = "2.0";
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

  window.fbq("init", pixelId);
  window.fbq("track", "PageView");
};

// Extend Window interface
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    fbq: any;
    _fbq: any;
  }
}

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (type: ConsentType, prefs?: CookiePreferences) => {
    localStorage.setItem(CONSENT_KEY, type || "");
    if (prefs) {
      localStorage.setItem(PREFERENCES_KEY, JSON.stringify(prefs));
    }
    setIsVisible(false);
    
    // Dispatch event for other components to react
    window.dispatchEvent(new CustomEvent("cookieConsentChanged", { detail: { type, preferences: prefs } }));

    // Update consent state for Google Analytics
    const analyticsGranted = prefs?.analytics || type === "all";
    const marketingGranted = prefs?.marketing || type === "all";
    updateGoogleConsent(analyticsGranted, marketingGranted);
  };

  const handleAcceptAll = () => {
    const allPrefs = { necessary: true, analytics: true, marketing: true };
    saveConsent("all", allPrefs);
  };

  const handleRejectAll = () => {
    const necessaryOnly = { necessary: true, analytics: false, marketing: false };
    saveConsent("necessary", necessaryOnly);
  };

  const handleClose = () => {
    // Closing without action = reject (as per Garante guidelines)
    handleRejectAll();
  };

  const handleSaveCustom = () => {
    saveConsent("custom", preferences);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
      >
        <div className="max-w-4xl mx-auto bg-background border border-border rounded-2xl shadow-2xl overflow-hidden">
          {!showCustomize ? (
            // Main Banner
            <div className="p-6 relative">
              {/* Close Button (X) - Garante 2024 requirement */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                aria-label="Chiudi senza accettare"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>

              <div className="flex items-start gap-4 pr-8">
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Cookie className="w-6 h-6 text-primary" />
                </div>
                <div className="flex-1 space-y-4">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-2">
                      Utilizziamo i cookie
                    </h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      Utilizziamo cookie tecnici e, previo tuo consenso, cookie di profilazione per analizzare 
                      il traffico, personalizzare contenuti e offrirti una migliore esperienza. 
                      I cookie analitici e di marketing vengono attivati <strong>solo dopo il tuo consenso esplicito</strong>.
                      Per maggiori informazioni consulta la nostra{" "}
                      <Link to="/privacy-policy" className="text-foreground underline font-semibold hover:text-primary">
                        Privacy Policy
                      </Link>{" "}
                      e la{" "}
                      <Link to="/cookie-policy" className="text-foreground underline font-semibold hover:text-primary">
                        Cookie Policy
                      </Link>.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <Button onClick={handleAcceptAll} className="rounded-full">
                      Accetta tutti
                    </Button>
                    <Button onClick={handleRejectAll} variant="outline" className="rounded-full">
                      Rifiuta
                    </Button>
                    <Button 
                      onClick={() => setShowCustomize(true)} 
                      variant="ghost" 
                      className="rounded-full gap-2"
                    >
                      <Settings className="w-4 h-4" />
                      Gestisci Preferenze
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Customize Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-foreground">
                  Gestisci le preferenze cookie
                </h3>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="w-8 h-8 rounded-full hover:bg-muted flex items-center justify-center transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-blue-500/5 border border-blue-500/20">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                      <p className="font-medium text-foreground">Cookie Necessari</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Essenziali per il funzionamento del sito. Sempre attivi.
                    </p>
                  </div>
                  <div className="w-12 h-6 rounded-full bg-blue-500 flex items-center justify-end px-1 ml-4">
                    <div className="w-4 h-4 rounded-full bg-white" />
                  </div>
                </div>

                {/* Analytics Cookies */}
                <label className="flex items-center justify-between p-4 rounded-xl bg-amber-500/5 border border-amber-500/20 cursor-pointer hover:bg-amber-500/10 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                      <p className="font-medium text-foreground">Cookie Analitici</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Ci aiutano a capire come utilizzi il sito (es. Google Analytics).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ml-4 ${
                    preferences.analytics ? 'bg-amber-500 justify-end' : 'bg-border justify-start'
                  }`}>
                    <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                  </div>
                </label>

                {/* Marketing Cookies */}
                <label className="flex items-center justify-between p-4 rounded-xl bg-purple-500/5 border border-purple-500/20 cursor-pointer hover:bg-purple-500/10 transition-colors">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                      <p className="font-medium text-foreground">Cookie di Marketing</p>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Utilizzati per mostrarti pubblicità pertinenti (es. Meta Pixel).
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="sr-only"
                  />
                  <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ml-4 ${
                    preferences.marketing ? 'bg-purple-500 justify-end' : 'bg-border justify-start'
                  }`}>
                    <div className="w-4 h-4 rounded-full bg-white shadow-sm" />
                  </div>
                </label>
              </div>

              <div className="flex gap-3">
                <Button onClick={handleSaveCustom} className="rounded-full flex-1">
                  Salva preferenze
                </Button>
                <Button onClick={handleAcceptAll} variant="outline" className="rounded-full">
                  Accetta tutti
                </Button>
              </div>

              <p className="text-xs text-muted-foreground text-center mt-4">
                Puoi modificare le tue preferenze in qualsiasi momento.{" "}
                <Link to="/cookie-policy" className="text-primary hover:underline">
                  Maggiori informazioni
                </Link>
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

// Export a button component to reopen preferences
export const CookiePreferencesButton = () => {
  const handleClick = () => {
    localStorage.removeItem(CONSENT_KEY);
    localStorage.removeItem(PREFERENCES_KEY);
    window.location.reload();
  };

  return (
    <button
      onClick={handleClick}
      className="text-sm text-background/60 hover:text-background transition-colors"
    >
      Gestisci Cookie
    </button>
  );
};
